import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Home, BookOpen, Bell, Settings, Menu, X } from 'lucide-react';
import HomePage from './pages/HomePage';
import GuidePage from './pages/GuidePage';
import NotificationPage from './pages/NotificationPage';
import AdminPage from './pages/AdminPage';

function Sidebar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const location = useLocation();

  const navItems = [
    { 
      path: '/', 
      icon: Home, 
      label: 'Home',
      activeClass: 'bg-gradient-to-r from-orange-500 to-amber-500'
    },
    { 
      path: '/guide', 
      icon: BookOpen, 
      label: 'Install Guide',
      activeClass: 'bg-gradient-to-r from-blue-500 to-cyan-500'
    },
    { 
      path: '/notifications', 
      icon: Bell, 
      label: 'Notifications',
      activeClass: 'bg-gradient-to-r from-purple-500 to-pink-500'
    },
    { 
      path: '/admin', 
      icon: Settings, 
      label: 'Admin Panel',
      activeClass: 'bg-gradient-to-r from-red-500 to-rose-500'
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen overflow-y-auto`}
      >
        {/* Safe area spacer for iOS status bar */}
        <div className="h-safe-area-inset-top bg-white" />
        
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-2xl">🧪</span>
              </div>
              <div>
                <h1 className="font-black text-gray-900 text-lg leading-tight">PWA Demo</h1>
                <p className="text-xs text-gray-500 font-semibold">Security Lab</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors tap-target touch-feedback"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4">
          <div className="space-y-1">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => window.innerWidth < 1024 && onClose()}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all touch-feedback tap-target ${
                    active
                      ? `${item.activeClass} text-white shadow-lg`
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className={`w-5 h-5 ${active ? 'text-white' : 'text-gray-400'}`} />
                  <span className="font-semibold">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Footer Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-gradient-to-br from-gray-50 to-white">
          <div className="text-xs text-gray-500 space-y-1">
            <p className="font-semibold text-gray-700">💡 PWA Features</p>
            <ul className="space-y-1 ml-4">
              <li>✓ Offline Support</li>
              <li>✓ Push Notifications</li>
              <li>✓ Home Screen Install</li>
              <li>✓ Security Logging</li>
            </ul>
          </div>
        </div>
      </aside>
    </>
  );
}

function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const location = useLocation();

  const getPageTitle = () => {
    switch (location.pathname) {
      case '/': return 'Home';
      case '/guide': return 'Installation Guide';
      case '/notifications': return 'Notifications';
      case '/admin': return 'Admin Panel';
      default: return 'PWA Demo';
    }
  };

  return (
    <div className="lg:hidden sticky top-0 z-30 bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg">
      {/* Safe area spacer for iOS notch/dynamic island */}
      <div className="h-safe-area-inset-top bg-gradient-to-r from-orange-500 to-amber-500" />
      <div className="flex items-center justify-between px-4 py-2.5">
        <button
          onClick={onMenuClick}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors active:scale-95 tap-target"
        >
          <Menu className="w-6 h-6" />
        </button>
        <h1 className="font-bold text-lg">{getPageTitle()}</h1>
        <div className="w-10" /> {/* Spacer for centering */}
      </div>
    </div>
  );
}

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <BrowserRouter>
      <div className="flex min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-screen">
          {/* Mobile Top Bar */}
          <TopBar onMenuClick={() => setSidebarOpen(true)} />

          {/* Page Content */}
          <main className="flex-1 overflow-x-hidden">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/guide" element={<GuidePage />} />
              <Route path="/notifications" element={<NotificationPage />} />
              <Route path="/admin" element={<AdminPage />} />
            </Routes>
          </main>

          {/* Bottom Safe Area (for mobile) */}
          <div className="h-safe-area-inset-bottom bg-transparent" />
        </div>
      </div>
    </BrowserRouter>
  );
}

export default App;
